/**
 * Pure geometry helpers for the animated Spin the Wheel UI.
 * Selection is chosen independently; these helpers only map index ↔ rotation.
 */

import type { RandomFn } from './selection.js';
import { WHEEL_TEXT_INNER_R, WHEEL_TEXT_OUTER_R } from './layout.js';

/**
 * Adaptive on-wheel text: full/short labels at or below this count.
 * Above this, segments use initials or numbers plus a legend.
 */
export const WHEEL_ON_WHEEL_LABEL_LIMIT = 16;

/** Counts at or below this use the longer “full” display-label budget. */
export const WHEEL_FULL_LABEL_LIMIT = 8;

/**
 * Counts at or below this (and above the text-label limit) use initials.
 * Above this, segments show legend numbers.
 */
export const WHEEL_INITIALS_LABEL_LIMIT = 24;

/** Default max characters for short on-wheel labels before ellipsis. */
export const WHEEL_LABEL_MAX_CHARS = 16;

/** Max characters for fuller on-wheel labels (few-strain pools). */
export const WHEEL_FULL_LABEL_MAX_CHARS = 22;

/**
 * Vivid repeating segment palette (coral → yellow → green → cyan → blue →
 * violet → pink → orange). Adjacent indices use distinct hues.
 */
export const WHEEL_VIVID_PALETTE = [
  '#E85D4C',
  '#F5C542',
  '#2BB673',
  '#2EC4B6',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#F97316',
] as const;

export type WheelLabelMode = 'full' | 'short' | 'initials' | 'number';

export type WheelSegmentInput = {
  id: string;
  /** Stored strain / display name (fallback when Product Name is missing). */
  name?: string | null;
  /** Catalogue Product Name — preferred visible wheel slice label. */
  productName?: string | null;
  /** Brand — never shown on the slice; used only for accessible distinction. */
  brand?: string | null;
  /** @deprecated Wheel segments are text-only; ignored when building labels. */
  imageUrl?: string | null;
};

export type WheelSegment = {
  index: number;
  id: string;
  /**
   * Visible wheel label before truncation (Product Name, with documented
   * fallbacks). Never includes brand or package-size display strings.
   */
  label: string;
  /**
   * Assistive / title label. Adds brand when it helps distinguish duplicates
   * without changing the visible slice text.
   */
  accessibleLabel: string;
  /** Truncated / mode-appropriate on-wheel text (single-line summary). */
  shortLabel: string;
  /** Fitted on-wheel lines (wrap-first, then truncate). */
  displayLines: string[];
  initials: string;
  /** 1-based legend number. */
  legendNumber: number;
  labelMode: WheelLabelMode;
  color: string;
  /** Always null — wheel segments no longer render product images. */
  imageUrl: null;
  /** Start angle in degrees from the top, clockwise (SVG wheel local space). */
  startDeg: number;
  endDeg: number;
  midDeg: number;
  /** True when full/short textual labels belong on the wheel. */
  showLabel: boolean;
};

export type WheelSpinPlan = {
  selectedIndex: number;
  targetRotation: number;
  durationMs: number;
  animate: boolean;
  extraTurns: number;
};

function trimLabelField(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Visible on-wheel label from catalogue Product Name with documented fallbacks.
 *
 * Order:
 * 1. `productName` (spreadsheet / DB Product Name)
 * 2. `name` (stored strain / concise display name)
 * 3. `Unnamed Product`
 */
export function resolveWheelVisibleLabel(strain: WheelSegmentInput): string {
  const productName = trimLabelField(strain.productName);
  if (productName) return productName;
  const name = trimLabelField(strain.name);
  if (name) return name;
  return 'Unnamed Product';
}

/**
 * Preferred accessible label for a single strain (brand when it helps).
 * Prefer {@link resolveWheelAccessibleLabelsForPool} when building a wheel so
 * duplicate Product Names stay unique across the pool.
 */
export function resolveWheelAccessibleLabel(strain: WheelSegmentInput): string {
  const visible = resolveWheelVisibleLabel(strain);
  const brand = trimLabelField(strain.brand);
  if (!brand) return visible;
  if (visible.toLowerCase().includes(brand.toLowerCase())) return visible;
  return `${visible} (${brand})`;
}

/**
 * Pool-aware accessible labels for every wheel segment.
 *
 * Visible slice text stays Product Name only. Accessible labels try, in order:
 * 1. Product Name (+ brand when useful);
 * 2. distinct stored `name` when it differs from the visible label;
 * 3. stored `name` + brand when that helps;
 * 4. stable `"{visible} (option N)"` using the 1-based pool index.
 *
 * Each returned label is unique within the pool (case-insensitive).
 */
export function resolveWheelAccessibleLabelsForPool(
  strains: readonly WheelSegmentInput[],
): string[] {
  const n = strains.length;
  if (n === 0) return [];

  const used = new Set<string>();
  const result: string[] = [];

  for (let i = 0; i < n; i += 1) {
    const strain = strains[i]!;
    const visible = resolveWheelVisibleLabel(strain);
    const brand = trimLabelField(strain.brand);
    const storedName = trimLabelField(strain.name);
    const candidates: string[] = [resolveWheelAccessibleLabel(strain)];

    if (storedName && storedName !== visible) {
      candidates.push(storedName);
      if (
        brand &&
        !storedName.toLowerCase().includes(brand.toLowerCase())
      ) {
        candidates.push(`${storedName} (${brand})`);
      }
    }

    candidates.push(`${visible} (option ${i + 1})`);

    let chosen = candidates[candidates.length - 1]!;
    for (const candidate of candidates) {
      const key = candidate.toLowerCase();
      if (!used.has(key)) {
        chosen = candidate;
        break;
      }
    }
    used.add(chosen.toLowerCase());
    result.push(chosen);
  }

  return result;
}

/**
 * Adaptive label mode from eligible pool size.
 */
export function resolveWheelLabelMode(count: number): WheelLabelMode {
  if (count <= WHEEL_FULL_LABEL_LIMIT) return 'full';
  if (count <= WHEEL_ON_WHEEL_LABEL_LIMIT) return 'short';
  if (count <= WHEEL_INITIALS_LABEL_LIMIT) return 'initials';
  return 'number';
}

/**
 * Character budget for a given label mode.
 */
export function maxCharsForWheelLabelMode(mode: WheelLabelMode): number {
  switch (mode) {
    case 'full':
      return WHEEL_FULL_LABEL_MAX_CHARS;
    case 'short':
      return WHEEL_LABEL_MAX_CHARS;
    case 'initials':
    case 'number':
      return 3;
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

/**
 * Initials for image-less or dense segments (up to two characters).
 */
export function wheelDisplayInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase();
}

/**
 * Segment fill colour. Avoids first/last collision when the count wraps the
 * palette such that the last wedge would match the first.
 */
export function wheelSegmentColor(index: number, count: number): string {
  const n = WHEEL_VIVID_PALETTE.length;
  let colorIndex = ((index % n) + n) % n;
  if (count > 1 && index === count - 1 && colorIndex === 0) {
    colorIndex = Math.floor(n / 2);
  }
  return WHEEL_VIVID_PALETTE[colorIndex]!;
}

/**
 * True when every pair of neighbouring segments (including first/last) use
 * different palette colours.
 */
export function wheelAdjacentColorsDistinct(count: number): boolean {
  if (count <= 1) return true;
  for (let i = 0; i < count; i += 1) {
    const a = wheelSegmentColor(i, count);
    const b = wheelSegmentColor((i + 1) % count, count);
    if (a === b) return false;
  }
  return true;
}

/**
 * True when a label stump ends with a dangling joiner such as `&`.
 */
export function wheelLabelEndsWithDanglingJoiner(text: string): boolean {
  return /(?:^|\s)[&/·–-]\s*…?$/.test(text.trim());
}

/**
 * Remove a trailing dangling joiner (`&`, `/`, etc.) before applying ellipsis.
 */
export function trimWheelLabelDanglingJoiner(text: string): string {
  let trimmed = text.trim().replace(/…$/, '').trimEnd();
  while (/(?:^|\s)[&/·–-]$/.test(trimmed)) {
    trimmed = trimmed.replace(/(?:^|\s)[&/·–-]$/, '').trimEnd();
  }
  return trimmed;
}

/**
 * Shorten a strain name for narrow wheel segments.
 * Prefers truncation at a word boundary when one falls in the latter half of
 * the allowed window; otherwise hard-truncates with an ellipsis.
 * Avoids leaving a visible stump that ends with a dangling `&`.
 */
export function shortenWheelLabel(
  name: string,
  maxChars: number = WHEEL_LABEL_MAX_CHARS,
): string {
  const trimmed = name.trim();
  if (trimmed.length <= maxChars) return trimmed;
  if (maxChars <= 1) return '…';
  const slice = trimmed.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(' ');
  const minBoundary = Math.floor(maxChars * 0.45);
  let stump =
    lastSpace >= minBoundary
      ? slice.slice(0, lastSpace).trimEnd()
      : trimmed.slice(0, Math.max(1, maxChars - 1)).trimEnd();
  stump = trimWheelLabelDanglingJoiner(stump);
  if (stump.length === 0) return '…';
  // If stripping joiners emptied past the budget, keep a hard truncate without `&`.
  if (stump.length > maxChars - 1) {
    stump = trimWheelLabelDanglingJoiner(
      stump.slice(0, Math.max(1, maxChars - 1)).trimEnd(),
    );
  }
  return `${stump}…`;
}

/**
 * Approximate characters that fit along the radial text band for one line.
 * Derived from viewBox radial span + mode font size (not a catalogue-wide
 * fixed Product Name budget).
 */
export function estimateWheelLabelLineCapacity(
  segmentCount: number,
  labelMode: WheelLabelMode,
): { maxCharsPerLine: number; maxLines: number } {
  const radialSpan = WHEEL_TEXT_OUTER_R - WHEEL_TEXT_INNER_R;
  const fontSize =
    labelMode === 'full' ? 14.5 : labelMode === 'short' ? 12 : 14;
  // Slightly generous average glyph width so common Product Names can use
  // two-line wrap before truncation.
  let maxCharsPerLine = Math.max(
    8,
    Math.floor(radialSpan / (fontSize * 0.5)),
  );
  const sweep = 360 / Math.max(segmentCount, 1);
  if (sweep < 22) maxCharsPerLine = Math.min(maxCharsPerLine, 10);
  else if (sweep < 30) maxCharsPerLine = Math.min(maxCharsPerLine, 14);
  const maxLines = labelMode === 'full' || labelMode === 'short' ? 2 : 1;
  return { maxCharsPerLine, maxLines };
}

/**
 * Prefer splitting before ` & word` so `&` stays with the following word.
 */
function splitBeforeAmpersandPhrase(
  text: string,
  maxCharsPerLine: number,
): string[] | null {
  const marker = ' & ';
  const idx = text.indexOf(marker);
  if (idx <= 0) return null;
  const before = text.slice(0, idx).trim();
  const after = text.slice(idx + 1).trim(); // "& Cheese"
  if (!before || !after) return null;
  if (before.length <= maxCharsPerLine && after.length <= maxCharsPerLine) {
    return [before, after];
  }
  return null;
}

/**
 * Split a label into at most two lines at a word boundary.
 * Prefers keeping `&` with the following word when that fits.
 */
export function splitWheelLabelLines(
  shortLabel: string,
  maxLineChars: number,
): string[] {
  const trimmed = shortLabel.trim();
  if (trimmed.length === 0) return [''];
  if (trimmed.length <= maxLineChars) return [trimmed];

  const ampSplit = splitBeforeAmpersandPhrase(trimmed, maxLineChars);
  if (ampSplit) return ampSplit;

  const space = trimmed.lastIndexOf(' ', maxLineChars);
  if (space >= Math.floor(maxLineChars * 0.4)) {
    let first = trimmed.slice(0, space).trim();
    const second = trimmed.slice(space + 1).trim();
    if (wheelLabelEndsWithDanglingJoiner(first)) {
      const earlier = first.lastIndexOf(' ');
      if (earlier >= Math.floor(maxLineChars * 0.35)) {
        const altFirst = first.slice(0, earlier).trim();
        const altSecond = `${first.slice(earlier + 1)} ${second}`.trim();
        if (
          altFirst &&
          altSecond &&
          altSecond.length <= maxLineChars * 1.25
        ) {
          return [altFirst, altSecond];
        }
      }
      first = trimWheelLabelDanglingJoiner(first);
    }
    if (first && second) return [first, second];
  }
  return [trimmed];
}

/**
 * Fit a Product Name into the radial text region: wrap first, truncate last.
 * Never leaves a visible stump ending with a dangling `&`.
 */
export function fitWheelLabelLines(
  label: string,
  options: {
    maxCharsPerLine: number;
    maxLines?: number;
  },
): string[] {
  const trimmed = label.trim();
  const maxCharsPerLine = Math.max(1, options.maxCharsPerLine);
  const maxLines = options.maxLines ?? 2;
  if (trimmed.length === 0) return [''];
  if (trimmed.length <= maxCharsPerLine) return [trimmed];

  if (maxLines >= 2) {
    const wrapped = splitWheelLabelLines(trimmed, maxCharsPerLine);
    if (
      wrapped.length === 2 &&
      wrapped[0]!.length <= maxCharsPerLine &&
      wrapped[1]!.length <= maxCharsPerLine &&
      !wheelLabelEndsWithDanglingJoiner(wrapped[0]!) &&
      !wheelLabelEndsWithDanglingJoiner(wrapped[1]!)
    ) {
      return wrapped;
    }
    // Second line may still be long — keep first wrap line, truncate the rest.
    if (wrapped.length === 2 && wrapped[0]!.length <= maxCharsPerLine) {
      let second = wrapped[1]!;
      if (second.length > maxCharsPerLine) {
        second = shortenWheelLabel(second, maxCharsPerLine);
      }
      if (
        !wheelLabelEndsWithDanglingJoiner(second) &&
        !wheelLabelEndsWithDanglingJoiner(wrapped[0]!)
      ) {
        return [wrapped[0]!, second];
      }
    }
  }

  return [shortenWheelLabel(trimmed, maxCharsPerLine)];
}

/**
 * Build equal wheel segments for eligible primary strains.
 * Label density adapts to catalogue size; geometry angles stay equal.
 */
export function buildWheelSegments(
  strains: readonly WheelSegmentInput[],
  options: {
    maxLabelChars?: number;
    onWheelLabelLimit?: number;
    labelMode?: WheelLabelMode;
  } = {},
): WheelSegment[] {
  const n = strains.length;
  if (n === 0) return [];
  const labelMode = options.labelMode ?? resolveWheelLabelMode(n);
  // Legacy override: callers may still force text off above a count.
  const onWheelLabelLimit =
    options.onWheelLabelLimit ?? WHEEL_ON_WHEEL_LABEL_LIMIT;
  const textAllowed =
    (labelMode === 'full' || labelMode === 'short') && n <= onWheelLabelLimit;
  const capacity = estimateWheelLabelLineCapacity(n, labelMode);
  // Prefer radial usable width; optional maxLabelChars remains an explicit override.
  const maxCharsPerLine =
    options.maxLabelChars ?? capacity.maxCharsPerLine;
  const sweep = 360 / n;
  const accessibleLabels = resolveWheelAccessibleLabelsForPool(strains);
  return strains.map((strain, index) => {
    const label = resolveWheelVisibleLabel(strain);
    const displayLines = textAllowed
      ? fitWheelLabelLines(label, {
          maxCharsPerLine,
          maxLines: capacity.maxLines,
        })
      : [labelMode === 'number' ? String(index + 1) : wheelDisplayInitials(label)];
    const shortLabel = displayLines.join(' ');
    const startDeg = index * sweep;
    const endDeg = (index + 1) * sweep;
    return {
      index,
      id: strain.id,
      label,
      accessibleLabel: accessibleLabels[index]!,
      shortLabel,
      displayLines,
      initials: wheelDisplayInitials(label),
      legendNumber: index + 1,
      labelMode,
      color: wheelSegmentColor(index, n),
      imageUrl: null,
      startDeg,
      endDeg,
      midDeg: startDeg + sweep / 2,
      showLabel: textAllowed,
    };
  });
}

/**
 * Absolute CSS rotation (degrees) that places `index` under the top pointer.
 *
 * Travel is computed relative to `currentRotation` so every spin advances by
 * `extraTurns` full rotations plus the segment offset needed to land on
 * `index` (not merely the next congruent landing below an absolute target).
 */
export function wheelRotationForIndex(
  index: number,
  segmentCount: number,
  options: {
    currentRotation?: number;
    extraTurns?: number;
  } = {},
): number {
  if (segmentCount <= 0) return options.currentRotation ?? 0;
  const sweep = 360 / segmentCount;
  const clamped = ((index % segmentCount) + segmentCount) % segmentCount;
  // Segment mid must land at the top pointer (0° in screen space after rotate).
  const landing = -(clamped * sweep + sweep / 2);
  const current = options.currentRotation ?? 0;
  const extraTurns = Math.max(0, options.extraTurns ?? 0);
  // Forward travel (more-negative rotation) congruent to the landing, in [0, 360).
  let phase = (current - landing) % 360;
  if (phase < 0) phase += 360;
  const travel = phase + 360 * extraTurns;
  return current - travel;
}
/**
 * Which segment index sits under the top pointer for a given rotation.
 */
export function wheelIndexAtRotation(
  rotationDeg: number,
  segmentCount: number,
): number {
  if (segmentCount <= 0) return -1;
  const sweep = 360 / segmentCount;
  // Invert the landing formula: pointer samples angle (-rotation) mod 360 from top.
  const angle = ((-rotationDeg % 360) + 360) % 360;
  const index = Math.floor(angle / sweep) % segmentCount;
  return index;
}

/**
 * Plan a spin: selection index is already chosen; animation only visualizes it.
 */
export function planWheelSpin(input: {
  selectedIndex: number;
  segmentCount: number;
  currentRotation?: number;
  preferReducedMotion?: boolean;
  random?: RandomFn;
}): WheelSpinPlan {
  const {
    selectedIndex,
    segmentCount,
    currentRotation = 0,
    preferReducedMotion = false,
    random = Math.random,
  } = input;

  if (segmentCount <= 0) {
    return {
      selectedIndex,
      targetRotation: currentRotation,
      durationMs: 0,
      animate: false,
      extraTurns: 0,
    };
  }

  if (preferReducedMotion) {
    const targetRotation = wheelRotationForIndex(selectedIndex, segmentCount, {
      currentRotation,
      extraTurns: 0,
    });
    return {
      selectedIndex,
      targetRotation,
      durationMs: 0,
      animate: false,
      extraTurns: 0,
    };
  }

  // Single eligible strain: short confirmational turn, not a fake multi-segment race.
  if (segmentCount === 1) {
    const extraTurns = 1;
    const targetRotation = wheelRotationForIndex(selectedIndex, segmentCount, {
      currentRotation,
      extraTurns,
    });
    return {
      selectedIndex,
      targetRotation,
      durationMs: 900,
      animate: true,
      extraTurns,
    };
  }

  const extraTurns = 4 + Math.floor(random() * 3); // 4–6 turns
  const targetRotation = wheelRotationForIndex(selectedIndex, segmentCount, {
    currentRotation,
    extraTurns,
  });
  return {
    selectedIndex,
    targetRotation,
    durationMs: 3800 + Math.floor(random() * 700),
    animate: true,
    extraTurns,
  };
}
